import React, { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

/* ═══════════════════════════════════════════════════════
   BusiGrow — First-Time Intro / Splash Animation
   Shows only once per session using sessionStorage.
   Full-screen cinematic reveal → logo → tagline → fade out.
   ═══════════════════════════════════════════════════════ */

const STORAGE_KEY = "busigrow_intro_seen";

/* ─── Animated particles ─── */
function Particles({ count = 40 }) {
  const particles = useRef(
    Array.from({ length: count }, (_, i) => ({
      id: i,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 4 + 1,
      delay: Math.random() * 2,
      duration: Math.random() * 3 + 2,
    }))
  ).current;

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {particles.map((p) => (
        <motion.div
          key={p.id}
          className="absolute rounded-full bg-white"
          style={{
            left: `${p.x}%`,
            top: `${p.y}%`,
            width: p.size,
            height: p.size,
          }}
          initial={{ opacity: 0, scale: 0 }}
          animate={{
            opacity: [0, 0.8, 0],
            scale: [0, 1.5, 0],
            y: [0, -60 - Math.random() * 80],
          }}
          transition={{
            duration: p.duration,
            delay: p.delay,
            ease: "easeOut",
          }}
        />
      ))}
    </div>
  );
}

/* ─── Animated rings ─── */
function PulseRings() {
  return (
    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
      {[0, 0.4, 0.8, 1.2].map((delay, i) => (
        <motion.div
          key={i}
          className="absolute rounded-full border border-white/20"
          initial={{ width: 40, height: 40, opacity: 0 }}
          animate={{
            width: [40, 500 + i * 100],
            height: [40, 500 + i * 100],
            opacity: [0, 0.6, 0],
          }}
          transition={{
            duration: 2.5,
            delay: 0.8 + delay,
            ease: "easeOut",
          }}
        />
      ))}
    </div>
  );
}

/* ─── Main Intro Component ─── */
export default function IntroAnimation({ onComplete }) {
  const [show, setShow] = useState(false);
  const [phase, setPhase] = useState("enter"); // enter → reveal → exit

  /* Internal play function — runs the full animation sequence */
  const playAnimation = React.useCallback(() => {
    setPhase("enter");
    setShow(true);
    document.body.style.overflow = "hidden";

    const revealTimer = setTimeout(() => setPhase("reveal"), 600);
    const exitTimer = setTimeout(() => setPhase("exit"), 3200);
    const doneTimer = setTimeout(() => {
      setShow(false);
      sessionStorage.setItem(STORAGE_KEY, "true");
      document.body.style.overflow = "";
      onComplete?.();
    }, 4000);

    return () => {
      clearTimeout(revealTimer);
      clearTimeout(exitTimer);
      clearTimeout(doneTimer);
      document.body.style.overflow = "";
    };
  }, [onComplete]);

  /* First-time play on mount */
  useEffect(() => {
    const seen = sessionStorage.getItem(STORAGE_KEY);
    if (seen) {
      onComplete?.();
      return;
    }
    return playAnimation();
  }, []);

  /* Listen for replay event from logo click */
  useEffect(() => {
    const handleReplay = () => {
      const cleanup = playAnimation();
      // Store cleanup so we can call it if replayed again quickly
      return cleanup;
    };
    window.addEventListener("busigrow:replay-intro", handleReplay);
    return () => window.removeEventListener("busigrow:replay-intro", handleReplay);
  }, [playAnimation]);

  if (!show) return null;

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          key="intro-overlay"
          className="fixed inset-0 z-[9999] flex items-center justify-center"
          style={{
            background:
              "radial-gradient(ellipse at center, #4c1d95 0%, #2e1065 40%, #1e1b4b 70%, #0f0a1e 100%)",
          }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.8, ease: [0.25, 0.46, 0.45, 0.94] }}
        >
          {/* Background animated gradient blobs */}
          <motion.div
            className="absolute inset-0 pointer-events-none"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1 }}
          >
            <div
              className="absolute top-[-20%] left-[-10%] w-[60vw] h-[60vw] rounded-full blur-[120px]"
              style={{
                background:
                  "radial-gradient(circle, rgba(139,92,246,0.4) 0%, transparent 70%)",
                animation: "intro-blob-1 6s ease-in-out infinite",
              }}
            />
            <div
              className="absolute bottom-[-20%] right-[-10%] w-[50vw] h-[50vw] rounded-full blur-[100px]"
              style={{
                background:
                  "radial-gradient(circle, rgba(167,139,250,0.3) 0%, transparent 70%)",
                animation: "intro-blob-2 8s ease-in-out infinite",
              }}
            />
            <div
              className="absolute top-[30%] right-[20%] w-[30vw] h-[30vw] rounded-full blur-[80px]"
              style={{
                background:
                  "radial-gradient(circle, rgba(196,181,253,0.2) 0%, transparent 70%)",
                animation: "intro-blob-1 5s ease-in-out infinite reverse",
              }}
            />
          </motion.div>

          {/* Particles */}
          <Particles count={50} />

          {/* Pulse rings */}
          <PulseRings />

          {/* Center content */}
          <div className="relative z-10 flex flex-col items-center text-center px-6">
            {/* Logo */}
            <motion.div
              initial={{ scale: 0, rotate: -20, opacity: 0 }}
              animate={
                phase === "enter"
                  ? { scale: 0, rotate: -20, opacity: 0 }
                  : phase === "reveal"
                  ? { scale: 1, rotate: 0, opacity: 1 }
                  : { scale: 1.1, rotate: 0, opacity: 0 }
              }
              transition={{
                duration: phase === "exit" ? 0.6 : 0.8,
                ease: [0.34, 1.56, 0.64, 1],
              }}
              className="mb-6"
            >
              <div className="relative">
                <img
                  src="/assets/images/Busigrow_Registered-1751003166599.png"
                  alt="BusiGrow"
                  className="h-16 sm:h-20 md:h-24 w-auto object-contain drop-shadow-[0_0_40px_rgba(139,92,246,0.6)]"
                />
                {/* Glow ring behind logo */}
                <motion.div
                  className="absolute inset-0 -m-4 rounded-full"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: [0, 0.5, 0] }}
                  transition={{ duration: 2, delay: 0.8, ease: "easeInOut" }}
                  style={{
                    boxShadow:
                      "0 0 60px 20px rgba(139,92,246,0.3), 0 0 120px 40px rgba(139,92,246,0.15)",
                  }}
                />
              </div>
            </motion.div>

            {/* Tagline line 1 */}
            <motion.h1
              className="text-white text-2xl sm:text-3xl md:text-5xl font-bold tracking-tight"
              initial={{ y: 40, opacity: 0, filter: "blur(10px)" }}
              animate={
                phase === "reveal"
                  ? { y: 0, opacity: 1, filter: "blur(0px)" }
                  : phase === "exit"
                  ? { y: -30, opacity: 0, filter: "blur(8px)" }
                  : {}
              }
              transition={{
                duration: phase === "exit" ? 0.5 : 0.9,
                delay: phase === "reveal" ? 0.3 : 0,
                ease: [0.25, 0.46, 0.45, 0.94],
              }}
            >
              We Build Brands
            </motion.h1>

            {/* Tagline line 2 */}
            <motion.h1
              className="text-transparent bg-clip-text bg-gradient-to-r from-violet-300 via-purple-300 to-fuchsia-300 text-2xl sm:text-3xl md:text-5xl font-bold tracking-tight mt-1"
              initial={{ y: 40, opacity: 0, filter: "blur(10px)" }}
              animate={
                phase === "reveal"
                  ? { y: 0, opacity: 1, filter: "blur(0px)" }
                  : phase === "exit"
                  ? { y: -30, opacity: 0, filter: "blur(8px)" }
                  : {}
              }
              transition={{
                duration: phase === "exit" ? 0.5 : 0.9,
                delay: phase === "reveal" ? 0.5 : 0.1,
                ease: [0.25, 0.46, 0.45, 0.94],
              }}
            >
              That Grow.
            </motion.h1>

            {/* Subtitle */}
            <motion.p
              className="text-violet-200/70 text-sm sm:text-base md:text-lg mt-4 max-w-md leading-relaxed"
              initial={{ y: 30, opacity: 0 }}
              animate={
                phase === "reveal"
                  ? { y: 0, opacity: 1 }
                  : phase === "exit"
                  ? { y: -20, opacity: 0 }
                  : {}
              }
              transition={{
                duration: phase === "exit" ? 0.4 : 0.8,
                delay: phase === "reveal" ? 0.8 : 0.15,
              }}
            >
              Branding &middot; Marketing &middot; Development &middot; AI
            </motion.p>

            {/* Animated loading bar */}
            <motion.div
              className="mt-8 w-32 sm:w-48 h-0.5 bg-white/10 rounded-full overflow-hidden"
              initial={{ opacity: 0 }}
              animate={{ opacity: phase === "reveal" ? 1 : 0 }}
              transition={{ duration: 0.3, delay: 0.9 }}
            >
              <motion.div
                className="h-full bg-gradient-to-r from-violet-400 via-purple-400 to-fuchsia-400 rounded-full"
                initial={{ width: "0%" }}
                animate={{ width: "100%" }}
                transition={{ duration: 2, delay: 1, ease: "easeInOut" }}
              />
            </motion.div>
          </div>

          {/* Skip button */}
          <motion.button
            className="absolute bottom-8 sm:bottom-12 text-white/30 text-xs sm:text-sm hover:text-white/60 transition-colors duration-300 z-20 px-4 py-2 rounded-full border border-white/10 hover:border-white/20 hover:bg-white/5 backdrop-blur-sm"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 1.5 }}
            onClick={() => {
              setShow(false);
              sessionStorage.setItem(STORAGE_KEY, "true");
              document.body.style.overflow = "";
              onComplete?.();
            }}
          >
            Skip intro →
          </motion.button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
